import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Login from "./Pages/Login/Login";
import { getToken, getUserRole } from "./Utils/Common";
import { hasRouteAccess } from "./Utils/AccessControl";
import Unauthorized from "./Components/Unautorized/Unauthorized";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Customer from "./Pages/Customer/Customer";
import ExpenseType from "./Pages/ExpenseType/ExpenseType";
// import Expense from "./Pages/Expense/Expense";
// import ExpenseForm from "./Pages/Expense/ExpenseForm";
import Branch from "./Pages/Branch/Branch";
import TestAndPrice from "./Pages/TestAndPrice/TestAndPrice";
import User from "./Pages/User/User";
import EndOfDay from "./Pages/EndOfDay/EndOfDay";
import SalesReport from "./Pages/Reports/SalesReport";
import ExpenseReport from "./Pages/Reports/ExpenseReport";
import ProfitAndLossReport from "./Pages/Reports/ProfitAndLossReport";
import AccountReceivableReport from "./Pages/Reports/AccountReceivableReport";
import PaymentReport from "./Pages/Reports/PaymentReport";
import EndOfDayForm from "./Pages/EndOfDay/EndOfDayForm";
import PurchaseRequest from "./Pages/PurchaseRequest/PurchaseRequest";
import PurchaseRequestForm from "./Pages/PurchaseRequest/PurchaseRequestForm";
import PurchaseOrder from "./Pages/PurchaseOrder/PurchaseOrder";
import PurchaseOrderForm from "./Pages/PurchaseOrder/PurchaseOrderForm";
import PettyCashEntry from "./Pages/PettyCash/PettyCashEntry";
import PettyCashEntryForm from "./Pages/PettyCash/PettyCashEntryForm";
import PettyCashRequisitionForm from "./Pages/PettyCash/PettyCashRequisition/PettyCashRequisitionForm";
import PettyCashRequisition from "./Pages/PettyCash/PettyCashRequisition/PettyCashRequisition";
import PaymentRequest from "./Pages/PaymentRequest/PaymentRequest";
import PaymentRequestForm from "./Pages/PaymentRequest/PaymentRequestForm";
import DailySalesReport from "./Pages/DailySalesReport/DailySalesReport";
import DailySalesReportForm from "./Pages/DailySalesReport/DailySalesReportForm";
import CreditBillingStatement from "./Pages/CreditBillingStatement/CreditBIllingStatement";
import CreditBillingStatementForm from "./Pages/CreditBillingStatement/CreditBillingStatementForm";
import CreditBillingPayment from "./Pages/CreditBillingPayment/CreditBillingPayment";
import CreditBillingPaymentForm from "./Pages/CreditBillingPayment/CreditBillingPaymentForm";
import Discount from "./Pages/Discount/Discount";
import BorrowedCash from "./Pages/BorrowedCash/BorrowedCash";
import AdvancePayment from "./Pages/AdvancePayment/AdvancePayment";
import DiscountForm from "./Pages/Discount/DiscountForm";

interface ProtectedRouteProps {
  element: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const location = useLocation();
  const token = getToken();
  const role = getUserRole();

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return hasRouteAccess(role, location.pathname) ? (
    <>{element}</>
  ) : (
    <Unauthorized />
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
        <Route path="/customer" element={<ProtectedRoute element={<Customer />} />} />
        <Route path="/expense-type" element={<ProtectedRoute element={<ExpenseType />} />} />
        <Route path="/test-price" element={<ProtectedRoute element={<TestAndPrice />} />} />
        <Route path="/user" element={<ProtectedRoute element={<User />} />} />
        <Route path="/petty-cash-entry" element={<ProtectedRoute element={<PettyCashEntry />} />} />
        <Route
          path="/petty-cash-requisition"
          element={<ProtectedRoute element={<PettyCashRequisition />} />}
        />
        <Route
          path="/petty-cash-out"
          element={<Navigate to="/petty-cash-requisition" replace />}
        />
        <Route
          path="/petty-cash-entry/add"
          element={<ProtectedRoute element={<PettyCashEntryForm add />} />}
        />
        <Route
          path="/petty-cash-entry/edit/:id"
          element={<ProtectedRoute element={<PettyCashEntryForm edit />} />}
        />
        <Route
          path="/petty-cash-entry/view/:id"
          element={<ProtectedRoute element={<PettyCashEntryForm view />} />}
        />
        <Route
          path="/petty-cash-entry/review/:id"
          element={<ProtectedRoute element={<PettyCashEntryForm review />} />}
        />
        <Route
          path="/petty-cash-requisition/add"
          element={<ProtectedRoute element={<PettyCashRequisitionForm add />} />}
        />
        <Route
          path="/petty-cash-requisition/view/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm view />} />}
        />
        <Route
          path="/petty-cash-requisition/review/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm approval />} />}
        />
        <Route
          path="/petty-cash-requisition/approval/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm approval />} />}
        />
        <Route
          path="/petty-cash-requisition/edit/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm edit />} />}
        />
        {/* Legacy petty-cash routes */}
        <Route
          path="/petty-cash/add"
          element={<ProtectedRoute element={<PettyCashRequisitionForm add />} />}
        />
        <Route
          path="/petty-cash/view/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm view />} />}
        />
        <Route
          path="/petty-cash/edit/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm edit />} />}
        />
        <Route
          path="/petty-cash/approval/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm approval />} />}
        />
        <Route
          path="/petty-cash/review/:id"
          element={<ProtectedRoute element={<PettyCashRequisitionForm approval />} />}
        />
        <Route
          path="/purchase-request"
          element={<ProtectedRoute element={<PurchaseRequest />} />}
        />
        <Route
          path="/purchase-request/add"
          element={<ProtectedRoute element={<PurchaseRequestForm add />} />}
        />
        <Route
          path="/purchase-request/edit/:id"
          element={<ProtectedRoute element={<PurchaseRequestForm edit />} />}
        />
        <Route
          path="/purchase-request/view/:id"
          element={<ProtectedRoute element={<PurchaseRequestForm view />} />}
        />
        <Route
          path="/purchase-request/approval/:id"
          element={<ProtectedRoute element={<PurchaseRequestForm approval />} />}
        />
        <Route
          path="/purchase-order"
          element={<ProtectedRoute element={<PurchaseOrder />} />}
        />
        <Route
          path="/purchase-order/add"
          element={<ProtectedRoute element={<PurchaseOrderForm add />} />}
        />
        <Route
          path="/purchase-order/edit/:id"
          element={<ProtectedRoute element={<PurchaseOrderForm edit />} />}
        />
        <Route
          path="/purchase-order/view/:id"
          element={<ProtectedRoute element={<PurchaseOrderForm view />} />}
        />
        <Route
          path="/purchase-order/approval/:id"
          element={<ProtectedRoute element={<PurchaseOrderForm approval />} />}
        />
        <Route path="/payment-request" element={<ProtectedRoute element={<PaymentRequest />} />} />
        <Route
          path="/payment-request/add"
          element={<ProtectedRoute element={<PaymentRequestForm add />} />}
        />
        <Route
          path="/payment-request/edit/:id"
          element={<ProtectedRoute element={<PaymentRequestForm edit />} />}
        />
        <Route
          path="/payment-request/view/:id"
          element={<ProtectedRoute element={<PaymentRequestForm view />} />}
        />
        <Route
          path="/payment-request/review/:id"
          element={<ProtectedRoute element={<PaymentRequestForm review />} />}
        />
        <Route
          path="/payment-request/approval/:id"
          element={<ProtectedRoute element={<PaymentRequestForm approval />} />}
        />
        <Route path="/branch" element={<ProtectedRoute element={<Branch />} />} />
        <Route path="/end-of-day" element={<ProtectedRoute element={<EndOfDay />} />} />
        <Route path="/end-of-day/add" element={<ProtectedRoute element={<EndOfDayForm add />} />} />
        <Route path="/end-of-day/view/:id" element={<ProtectedRoute element={<EndOfDayForm view />} />} />
        <Route path="/end-of-day/edit/:id" element={<ProtectedRoute element={<EndOfDayForm edit />} />} />
        <Route path="/end-of-day/check/:id" element={<ProtectedRoute element={<EndOfDayForm check />} />} />
        <Route path="/end-of-day/approval/:id" element={<ProtectedRoute element={<EndOfDayForm approval />} />} />
        <Route path="/daily-sales-report" element={<ProtectedRoute element={<DailySalesReport />} />} />
        <Route path="/daily-sales-report/edit/:id" element={<ProtectedRoute element={<DailySalesReportForm />} />} />
        <Route path="/daily-sales-report/view/:id" element={<ProtectedRoute element={<DailySalesReportForm />} />} />
        <Route
          path="/credit-billing-statement"
          element={<ProtectedRoute element={<CreditBillingStatement />} />}
        />
        <Route
          path="/credit-billing-statement/add"
          element={<ProtectedRoute element={<CreditBillingStatementForm add />} />}
        />
        <Route
          path="/credit-billing-statement/edit/:id"
          element={<ProtectedRoute element={<CreditBillingStatementForm edit />} />}
        />
        <Route
          path="/credit-billing-statement/review/:id"
          element={<ProtectedRoute element={<CreditBillingStatementForm review />} />}
        />
        <Route
          path="/credit-billing-statement/view/:id"
          element={<ProtectedRoute element={<CreditBillingStatementForm view />} />}
        />
        <Route
          path="/credit-billing-statement/approval/:id"
          element={<ProtectedRoute element={<CreditBillingStatementForm approval />} />}
        />
        <Route
          path="/credit-billing-payment"
          element={<ProtectedRoute element={<CreditBillingPayment />} />}
        />
        <Route
          path="/credit-billing-payment/add"
          element={<ProtectedRoute element={<CreditBillingPaymentForm add />} />}
        />
        <Route path="/discount" element={<ProtectedRoute element={<Discount />} />} />
        <Route path="/discounts/add" element={<ProtectedRoute element={<DiscountForm add />} />} />
        <Route path="/discounts/edit/:id" element={<ProtectedRoute element={<DiscountForm edit />} />} />
        <Route
          path="/borrowed-cash"
          element={<ProtectedRoute element={<BorrowedCash />} />}
        />
        <Route path="/sales-report" element={<ProtectedRoute element={<SalesReport />} />} />
        <Route path="/expense-report" element={<ProtectedRoute element={<ExpenseReport />} />} />
        <Route path="/profit-and-loss-report" element={<ProtectedRoute element={<ProfitAndLossReport />} />} />
        <Route path="/account-receivable-report" element={<ProtectedRoute element={<AccountReceivableReport />} />} />
        <Route path="/payment-report" element={<ProtectedRoute element={<PaymentReport />} />} />
        <Route path="/advance-payment" element={<ProtectedRoute element={<AdvancePayment />} />} />
      </Routes>
    </Router>
  );
}

export default App;
